import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@codex-brightworld/icons';
import styled, { css } from 'styled-components';

const StyledIcon = styled(Icon)`
  svg path:last-child {
    fill: ${({ theme }) => theme.colors.neutral.white};
  }
`;

export const StepNumber = styled.div`
  ${({
    theme,
    active, large
  }) => css`
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    margin: 0 10px 0 0;
    min-width: ${large ? '31px' : '21px'};
    width: ${large ? '31px' : '21px'};
    height: ${large ? '31px' : '21px'};
    outline: 5px solid #fff;
    border-radius: 50%;
    background-color: ${active
    ? theme.colors.semantic.green
    : theme.colors.neutral.grey[2]};
    box-shadow: 0 0 10px #fff;
    color: ${theme.colors.neutral.white};
    text-align: center;
    font-weight: bold;
    line-height: ${large ? '31px' : '21px'};

    .dc-icon {
        width: ${large ? '31px' : '21px'};
        height: ${large ? '31px' : '21px'};
        fill: ${theme.colors.neutral.white};
        margin-top: -2px;
    }
  `};
`;

export const StepContainer = styled.div`
  ${({ large, iframe, theme }) => css`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin: 10px 0 5px;
    min-height: 30px;

    &:first-child {
      margin-top: 7px;
    }

    &:last-child {
      min-height: 0;
    }

    &:after {
      position: absolute;
      top: 20px;
      left: ${large ? '15px' : '10px'};
      height: 100%;
      border-left: 2px solid ${theme.colors.neutral.grey[1]};
      content: "";
    }

    &:last-child:after {
      border: 0;
    }

    ${iframe && css`
        align-items: flex-start;
        ${StepNumber} {
          margin-top: 12px;
          border: 2px solid ${theme.colors.neutral.grey[1]};
        }
    `};
  `};
`;

export const StepContent = styled.div`
  flex-grow: 1;
  max-width: 100%;
  font-family: 'CurrysSans';
`;

const StepsContainer = styled.div`
  margin-bottom: 5px;
  ${({
    theme,
    horizontal, stepsCount
  }) => css`
    @media ${theme.breakpoints.mobile} {
      text-align: left;
    }

    ${horizontal && css`

      display: flex;
      align-items: baseline;
      text-align: center;
      ${StepContainer} {

        flex: 1 1;
        flex-direction: column;
        align-items: center;
        margin: 0 10px;

        &:after {
          top: 11px;
          left: 50%;
          width: 100%;
          height: auto;
          border-top: 2px solid ${theme.colors.neutral.grey[1]};
          border-left: none;
        }

        &:last-child:after {
          border: 0;
        }

        ${StepContent} {
          margin-top: 5px;
        };

        ${stepsCount && css`
          width: ${(100 / stepsCount)}%
        `};
      };
    `};
  `};
`;

export function Step(props) {
  return (
    <StepContainer
      large={props.large}
      iframe={props.iframe}
      data-qa={props.qa}
      data-active={props.active}
      className={props.className}
    >
      <StepNumber active={props.active} large={props.large}>
        {props.checked ? <StyledIcon icon="tick" /> : props.step}
      </StepNumber>
      <StepContent>{props.children}</StepContent>
    </StepContainer>
  );
}

Step.defaultProps = {
  large: false,
  iframe: false,
  active: false,
  checked: false,
  step: undefined,
  qa: undefined,
  className: undefined
};

Step.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.element,
    PropTypes.string
  ]).isRequired,
  large: PropTypes.bool,
  iframe: PropTypes.bool,
  active: PropTypes.bool,
  checked: PropTypes.bool,
  step: PropTypes.number,
  qa: PropTypes.string,
  className: PropTypes.string
};

export function Steps(props) {
  return (
    <StepsContainer
      horizontal={props.horizontal}
      stepsCount={props.children.filter(c => c).length}
      data-qa={props.qa}
      className={props.className}
    >
      {props.children.filter(c => c).map((child, i) => React.cloneElement(child, {
        key: i,
        step: i + 1,
        ...(typeof props.currentStep === 'number'
          ? {
            checked: i + 1 < props.currentStep,
            active: i < props.currentStep
          } : {})
      }))}
    </StepsContainer>
  );
}

Steps.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.array
  ]).isRequired,
  horizontal: PropTypes.bool,
  /**
   * If used, the `checked` and `active` props of child `Step`s are overridden so that the steps
   * before the current one are checked and active, the current step is active and the next steps
   * are neither active nor checked. 1-indexed (same as the `step` prop on `Step`s)
   */
  currentStep: PropTypes.number,
  qa: PropTypes.string,
  className: PropTypes.string
};

Steps.defaultProps = {
  horizontal: false,
  currentStep: undefined,
  qa: undefined,
  className: undefined
};
